import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Users as UsersIcon, KeyRound, Activity, LogOut, Search, X, Plus,
  ChevronUp, ChevronDown, ArrowLeft, Shield, Bot, Cpu, Mail, CalendarDays, Plug, History, GraduationCap, Gauge, LayoutGrid, Info, Check, AlertTriangle,
  Boxes, Settings, Menu,
} from "lucide-react";
import { apiGet, apiPost, returnToOwnCompany } from "../../Api/Client.js";
import { ATGLogo } from "../../Components/icons/icons.jsx";
import { McpScreen } from "../agents/McpScreen.jsx";
import { CompanySettingsCard, ModulesAndAddOns } from "./CompanyAdminCards.jsx";
import { AlumniRecordsView } from "../../Products/people/index.js";
import { CATEGORIES, featureCount, oursCount, vendorsFor } from "../../Shared/ModuleComparisonContent.js";
import { isJuniorType } from "../../Shared/ScreenUtils.jsx";
import "../../Styles/AdminPanel.css";

// Instant, not the browser's own `title=` delay — "on hover instantly" was
// the specific ask. Calendar and Gmail are two separate connections (an
// employee can have more than one of either), so this shows every account
// found for that person and its own status, not just a single yes/no.
//
// The tip is `position: fixed` and its coordinates are read from the icon's
// own bounding box on hover, then passed down as inline style — the table
// sits in an overflow-x:auto scroller, and CSS overflow-x alone quietly
// makes overflow-y auto too, so a plain `position: absolute` tip anchored to
// a cell can get clipped by the table's own scroll box. Fixed positioning
// against real viewport coordinates has no ancestor to get clipped by.
function ConnectionHoverTip({ icon: Icon, connected, label, connections }) {
  const [pos, setPos] = useState(null);
  const iconRef = React.useRef(null);

  const show = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
  };

  return (
    <span className="AdmX-ConnWrap" onMouseEnter={show} onMouseLeave={() => setPos(null)}>
      <Icon ref={iconRef} size={15} className={"AdmX-ConnIcon" + (connected ? " on" : "")} />
      {pos && (
        <div className="AdmX-ConnTip" style={{ top: pos.top, left: pos.left }}>
          <strong>{label}</strong>
          {connections.length ? (
            <ul>
              {connections.map((c, i) => (
                <li key={i}>
                  <span className="email">{c.email}</span>
                  <span className={"status" + (c.status === "connected" ? " on" : "")}>{c.status}</span>
                </li>
              ))}
            </ul>
          ) : <p className="none">Not connected.</p>}
        </div>
      )}
    </span>
  );
}

// The company admin panel (Entry 20, design A), at /admin/: one company only. Sections marked
// operatorOnly are platform-wide figures and show only to the platform operator.
const SECTIONS = [
  { key: "users", label: "People", icon: UsersIcon },
  { key: "modules", label: "Modules and add-ons", icon: Boxes },
  { key: "settings", label: "Company settings", icon: Settings },
  { key: "access", label: "Access model", icon: KeyRound },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "audit", label: "Login activity", icon: Activity },
  { key: "access_activity", label: "Access activity", icon: History },
  { key: "defaulters", label: "Defaulters", icon: AlertTriangle },
  { key: "aiusage", label: "AI Usage", icon: Cpu },
  { key: "mcp", label: "MCP agents", icon: Bot, addon: "axon" },
  { key: "alumni", label: "Alumni Records", icon: GraduationCap, addon: "people.alumni" },
];

const timeAgo = (iso) => {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (d < 1) return "today";
  if (d < 2) return "yesterday";
  if (d < 30) return `${Math.floor(d)}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
};

// What the People tab asks the server for. The server filters, sorts and pages (issue #163).
const PEOPLE_PAGE_SIZE = 50;
const FIRST_PEOPLE_QUERY = {
  q: "", status: "active", type: "all", department: "all",
  sort: { k: "name", dir: "asc" }, page: 1, pageSize: PEOPLE_PAGE_SIZE,
};
const peopleUrl = (qy) => {
  const p = new URLSearchParams();
  p.set("page", String(qy.page));
  p.set("page_size", String(qy.pageSize));
  p.set("status", qy.status);
  p.set("type", qy.type);
  if (qy.department && qy.department !== "all") p.set("department", qy.department);
  if (qy.q) p.set("q", qy.q);
  p.set("sort", (qy.sort.dir === "desc" ? "-" : "") + qy.sort.k);
  return `/Users/admin-panel/users/?${p.toString()}`;
};

export function AdminPanelScreen({ data, navigate, logout, reload }) {
  const isOperator = !!data?.me?.user?.isOperator;
  const [addonsOn, setAddonsOn] = useState(null);
  useEffect(() => {
    apiGet("/EnterpriseCore/plugins/")
      .then((res) => setAddonsOn(new Set((res?.plugins || []).filter((p) => p.enabled).map((p) => p.code))))
      .catch(() => setAddonsOn(new Set()));
  }, []);
  const sections = SECTIONS.filter((s) => {
    if (s.operatorOnly && !isOperator) return false;
    if (s.addon && (!addonsOn || !addonsOn.has(s.addon))) return false;
    return true;
  });
  const [section, setSectionState] = useState(() => {
    const asked = new URLSearchParams(window.location.search).get("section");
    return sections.some((s) => s.key === asked) ? asked : "users";
  });
  // On a phone or narrow window the section list slides in over the page; this is its open state.
  const [navOpen, setNavOpen] = useState(false);
  const setSection = (key) => {
    setSectionState(key);
    setNavOpen(false);
    try { window.history.replaceState(null, "", key === "users" ? "/company-admin/" : `/company-admin/?section=${key}`); } catch (e) {}
  };
  const support = data?.me?.support || null;
  const leaveSupport = async () => {
    // If the visit could not be closed it is still open on the server: say so and stay, rather than
    // leaving the operator believing they are out.
    try {
      await apiPost(`/EnterpriseCore/platform/support/${support.id}/close/`, {});
    } catch (e) {
      setErr(e?.payload?.detail || "Could not leave this company. Please try again.");
      return;
    }
    // Entry 20 review: back to the operator's own company, not the "1" default a removed id falls to.
    try { returnToOwnCompany(data?.me?.user?.ownCompanyId); } catch (e) {}
    window.location.assign("/platform/");
  };
  // The People tab shows ONE PAGE of the company's people; search, filters, sort and paging are
  // done by the server, so the tab opens as fast for 5,000 people as for 50 (issue #163).
  const [people, setPeople] = useState({ users: [], count: 0, page: 1, pages: 1, departments: [] });
  const [peopleQuery, setPeopleQuery] = useState(FIRST_PEOPLE_QUERY);
  const [peopleBusy, setPeopleBusy] = useState(true);
  const peopleSeq = React.useRef(0);
  const [matrix, setMatrix] = useState(null);
  // null = not loaded yet (shows "Loading…"), so an empty list always means the server said "none".
  const [audit, setAudit] = useState(null);
  const [accessActivity, setAccessActivity] = useState(null);
  const [defaulters, setDefaulters] = useState(null);
  const [defaultersDays, setDefaultersDays] = useState(30);
  const [health, setHealth] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [aiDays, setAiDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [accessUser, setAccessUser] = useState(null);

  const me = data?.me?.user || {};
  const meName = me.fullName || me.full_name || me.username || "Admin";

  const load = async () => {
    setLoading(true); setErr("");
    try {
      setMatrix(await apiGet("/Users/admin-panel/matrix/"));
    } catch (e) { setErr(e?.payload?.detail || e?.message || "Only your company's admins can open this panel."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Fetch the page the query describes. Answers can arrive out of order while someone is typing or
  // clicking, so only the latest request is allowed to land.
  const loadPeople = async (query) => {
    const mine = ++peopleSeq.current;
    setPeopleBusy(true);
    try {
      const r = await apiGet(peopleUrl(query));
      if (mine === peopleSeq.current) setPeople(r);
    } catch (e) {
      if (mine === peopleSeq.current) setErr(e?.payload?.detail || e?.message || "Only your company's admins can open this panel.");
    }
    if (mine === peopleSeq.current) setPeopleBusy(false);
  };
  useEffect(() => { loadPeople(peopleQuery); }, [peopleQuery]);
  // A failed load is shown as a failure with a retry, never as an empty result: "Nothing yet" or
  // "No defaults" must only appear when the server really answered with nothing.
  const [tabError, setTabError] = useState({});
  const [tabRetry, setTabRetry] = useState(0);
  const loadTab = (key, url, onData) => {
    setTabError((cur) => ({ ...cur, [key]: "" }));
    return apiGet(url).then(onData).catch((e) => {
      setTabError((cur) => ({ ...cur, [key]: e?.payload?.detail || "This could not be loaded right now." }));
    });
  };
  useEffect(() => {
    if (section === "audit" && audit === null)
      loadTab("audit", "/Users/admin-panel/login-audit/", (a) => setAudit(a.logins || []));
  }, [section, tabRetry]);
  useEffect(() => {
    if (section === "access_activity" && accessActivity === null)
      loadTab("access_activity", "/Users/admin-panel/access-activity/", (a) => setAccessActivity(a.events || []));
  }, [section, tabRetry]);
  useEffect(() => {
    if (section !== "defaulters") return;
    setDefaulters(null);
    loadTab("defaulters", `/Users/admin-panel/defaulters/?days=${defaultersDays}`, setDefaulters);
  }, [section, defaultersDays, tabRetry]);
  // Fetched on mount, not gated to the Integrations section — the sidebar
  // itself needs to know whether anything's down (2026-08-13: "the left
  // panel bar of integrations should show up in red" when it is), which
  // means this can't wait for the user to click into that section first.
  // The backend already caches this response for 45s, so polling it
  // unconditionally here costs nothing extra it wasn't already paying.
  useEffect(() => {
    loadTab("integrations", "/Users/admin-panel/integrations-health/", setHealth);
  }, [tabRetry]);
  useEffect(() => {
    if (section === "aiusage")
      loadTab("aiusage", `/Users/admin-panel/ai-usage/?days=${aiDays}`, setAiUsage);
  }, [section, aiDays, tabRetry]);
  useEffect(() => {
    if (section === "endpoint_usage")
      loadTab("endpoint_usage", `/Users/admin-panel/endpoint-usage/?days=${endpointDays}`, setEndpointUsage);
  }, [section, endpointDays, tabRetry]);


  // The server returns the person's freshly resolved access, and ALL of it has
  // to land back in state. The access grid is rendered from page_access and
  // page_exceptions; if those are not copied across, ticking a box writes the
  // grant on the server and changes nothing on screen — which reads as "the
  // click did nothing", and clicking again silently toggles it back off.
  const applyActionResult = (target, r) => ({
    ...target,
    is_active: r.is_active ?? target.is_active,
    is_superuser: r.is_superuser ?? target.is_superuser,
    overrides: r.overrides ?? target.overrides,
    page_access: r.page_access ?? target.page_access,
    page_overrides: r.page_overrides ?? target.page_overrides,
  });

  const act = async (id, body) => {
    try {
      const r = await apiPost(`/Users/admin-panel/users/${id}/action/`, body);
      setPeople((prev) => ({ ...prev, users: prev.users.map((u) => (u.id === id ? applyActionResult(u, r) : u)) }));
      setAccessUser((a) => (a && a.id === id ? applyActionResult(a, r) : a));
      // Switching someone off (or making them an operator) can take them out of the filtered view
      // or move them in the sort, which only the server can tell: ask it for the page again.
      if (body.action === "set_active" || body.action === "set_superuser") loadPeople(peopleQuery);
    } catch (e) { alert(e?.payload?.detail || e?.message || "Failed."); }
  };

  // The drawer opens at once on the row's page grid; the long parts (hand-set exceptions) come
  // from one person's detail, and the grid stays locked until they have arrived.
  const openAccess = async (u) => {
    setAccessUser({ ...u, detailLoading: true });
    try {
      const d = await apiGet(`/Users/admin-panel/users/${u.id}/`);
      setAccessUser((a) => (a && a.id === u.id ? { ...a, ...d, detailLoading: false } : a));
    } catch (e) {
      const why = e?.payload?.detail || e?.message || "Could not load this person's access.";
      setAccessUser((a) => (a && a.id === u.id ? { ...a, detailLoading: false, detailError: why } : a));
    }
  };

  const impersonate = async (u) => {
    if (u.is_superuser) { alert("Can't log in as another superuser."); return; }
    if (!window.confirm(`Log in as ${u.name}?\n\nYou'll see the whole app exactly as they do — their nav, what they can open, and their masked emails/phones. Use "Exit" in the top bar to come back to yourself.`)) return;
    try {
      await apiPost(`/Users/admin-panel/users/${u.id}/impersonate/`, {});
      // Remembered so "Exit — back to admin" (App.jsx) returns here instead
      // of defaulting to /home/ — that was landing the admin on the target
      // user's own homepage, not back on this People list.
      try { sessionStorage.setItem("atg-impersonate-return-to", "/company-admin/"); } catch (e) {}
      window.location.href = "/home/"; // full reboot as the target user
    } catch (e) { alert(e?.payload?.detail || e?.message || "Could not log in as that user."); }
  };

  return (
    <div className="AdmX">
      {navOpen && <div className="AdmX-Scrim" onClick={() => setNavOpen(false)} />}
      <aside className={"AdmX-Nav" + (navOpen ? " open" : "")}>
        <div className="AdmX-Brand"><ATGLogo company={data?.me?.company} /><span>{data?.me?.activeTenant?.name || "Company"} admin</span></div>
        <nav className="AdmX-NavList">
          {sections.map((s) => (
            <button
              key={s.key}
              className={"AdmX-NavItem" + (section === s.key ? " on" : "") + (s.key === "integrations" && health?.any_down ? " danger" : "")}
              onClick={() => setSection(s.key)}
            >
              <s.icon size={17} /> {s.label}
            </button>
          ))}
        </nav>
        <div className="AdmX-NavFoot">
          <button className="AdmX-Exit" onClick={() => navigate("/home/")}><ArrowLeft size={16} /> Exit admin</button>
          <div className="AdmX-Me">
            <span className="AdmX-MeAv">{meName.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}</span>
            <span className="AdmX-MeName">{meName}</span>
            <button className="AdmX-MeOut" onClick={logout} title="Log out"><LogOut size={15} /></button>
          </div>
        </div>
      </aside>

      <main className="AdmX-Main">
        <div className="AdmX-MobileBar">
          <button type="button" className="AdmX-NavToggle" onClick={() => setNavOpen(true)} aria-label="Open the section menu" aria-expanded={navOpen}>
            <Menu size={18} /> {sections.find((x) => x.key === section)?.label}
          </button>
        </div>
        {support && (
          <div className="AdmX-Err" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <span>You are inside {data?.me?.activeTenant?.name} as platform support until {new Date(support.endsAt).toLocaleTimeString()}. Changes you make here are recorded in their trail.</span>
            <button className="AdmX-Exit" onClick={leaveSupport}>Leave this company</button>
          </div>
        )}
        {err && <div className="AdmX-Err">{err}</div>}
        {loading ? <div className="AdmX-Loading">Loading…</div> : tabError[section] ? (
          <div className="AdmX-View">
            <div className="AdmX-Err" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <span>{tabError[section]}</span>
              <button className="AdmX-Exit" onClick={() => setTabRetry((n) => n + 1)}>Try again</button>
            </div>
          </div>
        ) : (
          <>
            {section === "users" && <PeopleView people={people} query={peopleQuery} setQuery={setPeopleQuery} busy={peopleBusy} act={act} onAccess={openAccess} onImpersonate={impersonate} activeDepartments={data.activeDepartments} isOperator={isOperator} />}
            {section === "modules" && <ModulesAndAddOns onPluginsChanged={() => reload && reload(["me"])} />}
            {section === "settings" && <CompanySettingsCard onChanged={() => reload && reload(["me"])} />}
            {section === "access" && <AccessModelView matrix={matrix} />}
            {section === "integrations" && <IntegrationsHealthView health={health} />}
            {section === "audit" && <AuditView audit={audit} />}
            {section === "access_activity" && <AccessActivityView events={accessActivity} />}
            {section === "defaulters" && <DefaultersView report={defaulters} days={defaultersDays} setDays={setDefaultersDays} />}
            {section === "aiusage" && <AiUsageView usage={aiUsage} days={aiDays} setDays={setAiDays} />}
            {section === "mcp" && <McpScreen data={data} reload={reload} />}
            {section === "alumni" && <AlumniRecordsView />}
          </>
        )}
      </main>

      {accessUser && (
        <AccessDrawer user={accessUser} matrix={matrix}
          onClose={() => setAccessUser(null)} act={act} />
      )}
    </div>
  );
}

/* ── sortable header cell ── */
function Th({ label, k, sort, setSort, align, w }) {
  const active = sort.k === k;
  return (
    <th className={"AdmX-Th" + (align === "r" ? " r" : "")} style={w ? { width: w } : undefined}
      onClick={() => setSort({ k, dir: active && sort.dir === "asc" ? "desc" : "asc" })}>
      <span>{label}{active && (sort.dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}</span>
    </th>
  );
}

function PeopleView({ people, query, setQuery, busy, act, onAccess, onImpersonate, activeDepartments, isOperator }) {
  const [loginAs, setLoginAs] = useState(false);  // "Login as" pick-a-person mode
  const users = people.users || [];
  const sort = query.sort;
  // Any change to what is asked for starts again from the first page.
  const ask = (change) => setQuery((cur) => ({ ...cur, ...change, page: 1 }));
  const setSort = (next) => ask({ sort: next });

  // The search box answers a moment after typing stops, not on every key: each answer is a trip
  // to the server, and a company can have thousands of people.
  const [text, setText] = useState(query.q);
  useEffect(() => {
    const typed = text.trim();
    if (typed === query.q) return undefined;
    const wait = setTimeout(() => ask({ q: typed }), 300);
    return () => clearTimeout(wait);
  }, [text]);

  // Candidate names come from the one shared active-departments list, not from the people
  // themselves — a long-exited employee can still carry a stale/archived department name, and
  // that must not resurrect it as a filter option here. Sorted by headcount, most people first
  // (alphabetical made a department with one person sit above one with thirty).
  const activeDepartmentNames = useMemo(
    () => new Set((activeDepartments || []).map((d) => d.name)),
    [activeDepartments],
  );
  const deptOptions = useMemo(
    () => (people.departments || [])
      .filter((d) => activeDepartmentNames.has(d.name))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .map((d) => d.name),
    [people.departments, activeDepartmentNames],
  );

  const from = people.count ? (people.page - 1) * query.pageSize + 1 : 0;
  const to = Math.min(people.page * query.pageSize, people.count);
  const goToPage = (page) => setQuery((cur) => ({ ...cur, page }));

  return (
    <div className="AdmX-View">
      <div className="AdmX-Head"><h1>People</h1><span className="AdmX-Sub">{people.count}</span></div>
      <div className="AdmX-Filters">
        <div className="AdmX-SearchBox"><Search size={15} /><input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search people" /></div>
        <select value={query.status} onChange={(e) => ask({ status: e.target.value })}>
          <option value="active">Active</option><option value="inactive">Deactivated</option><option value="all">All</option>
        </select>
        <select value={query.type} onChange={(e) => ask({ type: e.target.value })}>
          <option value="all">All types</option><option value="ft">Full-time</option><option value="intern">Juniors</option>
        </select>
        <select value={query.department} onChange={(e) => ask({ department: e.target.value })} title="Filter by department">
          <option value="all">All departments</option>
          {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        {isOperator && <button
          onClick={() => setLoginAs((v) => !v)}
          title="View the app exactly as another person sees it"
          style={{ marginLeft: "auto", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontWeight: 600,
            border: "1px solid " + (loginAs ? "var(--blue-500,#5468FF)" : "var(--c-200,#333)"),
            background: loginAs ? "var(--blue-500,#5468FF)" : "transparent",
            color: loginAs ? "#fff" : "var(--c-600,#aaa)" }}>
          {loginAs ? "Pick a person… (cancel)" : "Login as…"}
        </button>}
      </div>
      {loginAs && <div className="AdmX-LoginAsHint" style={{ margin: "0 0 8px", fontSize: 13, color: "var(--blue-600,#7d8cff)" }}>
        ▸ Click any person below to view the whole app as them — their nav, access, and masked contact details. You can exit from the top bar afterwards.
      </div>}
      <div className="Table-Scroll" style={busy && users.length ? { opacity: 0.6 } : undefined}>
        <table className="AdmX-Table">
          <thead><tr>
            <Th label="Name" k="name" sort={sort} setSort={setSort} />
            <Th label="Department" k="department" sort={sort} setSort={setSort} />
            <Th label="Family" k="family" sort={sort} setSort={setSort} />
            <Th label="Type" k="employment_type" sort={sort} setSort={setSort} />
            <th className="AdmX-Th r" style={{ width: 36 }} title="Gmail connected"><Mail size={14} /></th>
            <th className="AdmX-Th r" style={{ width: 36 }} title="Google Calendar connected"><CalendarDays size={14} /></th>
            <Th label="Pages" k="page_count" sort={sort} setSort={setSort} align="r" />
            <Th label="Active" k="is_active" sort={sort} setSort={setSort} />
            <Th label="Last seen" k="last_login" sort={sort} setSort={setSort} align="r" />
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={u.is_active ? "" : "off"}>
                <td className="nm"
                  onClick={loginAs && !u.is_superuser ? () => onImpersonate(u) : undefined}
                  title={loginAs ? (u.is_superuser ? "Can't log in as a superuser" : `Login as ${u.name}`) : undefined}
                  style={loginAs ? { cursor: u.is_superuser ? "not-allowed" : "pointer" } : undefined}>
                  <b style={loginAs && !u.is_superuser ? { color: "var(--blue-600,#7d8cff)", textDecoration: "underline" } : undefined}>{u.name}</b><i>{u.username}</i>
                </td>
                <td>{u.department || <em>—</em>}</td>
                <td>{u.family ? <span className="fam">{u.family_label || u.family}</span> : <em>—</em>}</td>
                <td><span className={"typ " + (isJuniorType(u.employment_type) ? "in" : "ft")}>{u.employment_type || "—"}</span></td>
                <td className="r">
                  <ConnectionHoverTip icon={Mail} connected={u.gmail_connected} label="Gmail" connections={u.gmail_connections || []} />
                </td>
                <td className="r">
                  <ConnectionHoverTip icon={CalendarDays} connected={u.calendar_connected} label="Google Calendar" connections={u.calendar_connections || []} />
                </td>
                <td className="r">
                  {(() => {
                    // Show "role baseline + extra PAGES" (e.g. 10 +2), both at the
                    // page level, so the two numbers are comparable. The old form
                    // was total-pages + a hand-override *action* count (12 +6): two
                    // different granularities that read as inconsistent, and it hid
                    // extras granted via capability grants (which aren't page
                    // overrides) entirely. Extra now counts every page beyond the
                    // role baseline, whatever mechanism granted it.
                    const total = u.page_count ?? 0;
                    const base = u.baseline_page_count ?? total;
                    const extra = total - base;
                    const title = u.is_superuser
                      ? "Superuser — all pages"
                      : `${base} from role${extra > 0 ? ` + ${extra} extra` : ""} = ${total} pages`;
                    return (
                      <button className="acc" onClick={() => onAccess(u)} title={title}>
                        {u.is_superuser ? "all" : base}
                        {!u.is_superuser && extra > 0 && <sup>+{extra}</sup>}
                        {!u.is_superuser && extra < 0 && <sup>&minus;{-extra}</sup>}
                      </button>
                    );
                  })()}
                </td>
                <td>
                  <Toggle on={u.is_active} onClick={() => act(u.id, { action: "set_active", value: !u.is_active })} />
                  {u.is_active && u.hr_status === "Exited" && (
                    <span className="AdmX-ExitedWarn" title="HR marked this person Exited, but their login was never deactivated.">
                      ⚠ Exited
                    </span>
                  )}
                </td>
                <td className="r sub">{timeAgo(u.last_login)}</td>
              </tr>
            ))}
            {!users.length && <tr><td colSpan={10} className="AdmX-Empty">{busy ? "Loading…" : "No people match."}</td></tr>}
          </tbody>
        </table>
      </div>
      {people.count > 0 && (
        <div className="AdmX-Pager">
          <span>{from}–{to} of {people.count}</span>
          <button disabled={busy || people.page <= 1} onClick={() => goToPage(people.page - 1)}>Previous</button>
          <span>Page {people.page} of {people.pages}</span>
          <button disabled={busy || people.page >= people.pages} onClick={() => goToPage(people.page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}

function Toggle({ on, onClick, danger }) {
  return <button className={"AdmX-Tg" + (on ? (danger ? " on danger" : " on") : "")} onClick={onClick}><span /></button>;
}

function AccessModelView({ matrix }) {
  if (!matrix) return null;
  const actions = matrix.actions || ["create", "read", "update", "delete"];
  const pages = matrix.pages || [];

  // A permission is a page in the left nav; what you may do with it is four
  // flags. So the model reads as a grid, not a list of invented codes.
  const Grid = ({ grid, emptyLabel }) => {
    const held = pages.filter((p) => (grid[p.page] || []).length);
    if (!held.length) return <em>{emptyLabel || "nothing"}</em>;
    return (
      <table className="AdmX-PageGrid">
        <thead>
          <tr>
            <th>Page</th>
            {actions.map((a) => <th key={a} title={a}>{a[0].toUpperCase()}</th>)}
          </tr>
        </thead>
        <tbody>
          {held.map((p) => {
            const has = grid[p.page] || [];
            return (
              <tr key={p.page}>
                <td title={p.note || p.label}>
                  {p.label}
                  {p.self_service && <span className="AdmX-SelfTag" title="Everyone gets this — it holds their own records">own</span>}
                </td>
                {actions.map((a) => (
                  <td key={a} className="AdmX-Flag">
                    {!p.supports.includes(a)
                      ? <span className="AdmX-NA" title={`${p.label} has no ${a}`}>·</span>
                      : has.includes(a)
                        ? <span className="AdmX-Yes" title={`${p.page} — ${a}`}>✓</span>
                        : <span className="AdmX-No" title={`${p.page} — no ${a}`}>–</span>}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="AdmX-View">
      <div className="AdmX-Head">
        <h1>Access model</h1>
        <span className="AdmX-Sub">page · create / read / update / delete</span>
      </div>
      <p className="AdmX-Rule">{matrix.page_rule}</p>
      <div className="AdmX-Model">
        <div className="AdmX-Fam base">
          <div className="AdmX-FamHead"><b>Everyone</b><i>any department, including interns</i></div>
          <Grid grid={matrix.everyone || {}} />
        </div>
        <div className="AdmX-Fam base">
          <div className="AdmX-FamHead"><b>Every full-timer</b><i>any department</i></div>
          <Grid grid={matrix.everyone_full_time || {}} />
        </div>
        {(matrix.page_families || []).map((f) => (
          <div className="AdmX-Fam" key={f.family}>
            <div className="AdmX-FamHead"><b>{f.label || f.family}</b><i>{f.departments.join(" · ") || "—"}</i></div>
            <div className="AdmX-FamGrid">
              <label>Juniors</label><Grid grid={f.intern} emptyLabel="everyone-baseline only" />
              <label>Full-time</label><Grid grid={f.full_time} emptyLabel="everyone-baseline only" />
            </div>
          </div>
        ))}
      </div>
      <p className="AdmX-Rule dim">
        A page with no tick anywhere is not visible to that seat at all, and its
        endpoints refuse the request — not only in the menu, but to anything calling
        the API. A dot means the page has no such button to grant.
      </p>
    </div>
  );
}

function IntegrationsHealthView({ health }) {
  if (!health) return <div className="AdmX-Loading">Loading…</div>;
  const rows = health.integrations || [];
  const STATUS_LABEL = { up: "Up", down: "Down", not_configured: "Not configured" };
  return (
    <div className="AdmX-View">
      <div className="AdmX-Head">
        <h1>Integrations</h1>
        <span className="AdmX-Sub">
          {health.any_down ? `${health.down.join(", ")} not doing anything right now` : "All checked integrations are up"}
        </span>
      </div>
      <div className="AdmX-Model">
        {rows.map((row) => (
          <div className="AdmX-Fam" key={row.name}>
            <div className="AdmX-FamHead">
              <b>
                {/* "not_configured" reads as red too, the same weight as "down" —
                    to an admin checking this page, a key someone never set and a
                    key someone accidentally cleared both mean the integration
                    isn't doing anything right now, which is the thing worth
                    flagging. The text underneath still says which one it is. */}
                <span
                  style={{
                    display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 8,
                    background: row.status === "up" ? "var(--green-500)" : "var(--red-500)",
                  }}
                />
                {row.name}
              </b>
              <i>{STATUS_LABEL[row.status] || row.status}</i>
            </div>
            <div style={{ padding: "11px 16px", fontSize: 13, color: "var(--c-600)" }}>
              {row.detail}
              <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--c-400)" }}>
                Checked {timeAgo(row.checked_at)}
                {row.last_used && ` · last used ${timeAgo(row.last_used)}`}
              </div>
            </div>

            {/* How much has actually crossed. A status dot says the pipe is
                open; these say what went through it. */}
            {!!row.volume?.length && (
              <div className="AdmX-Vol">
                {row.volume.map(([label, value]) => (
                  <div className="AdmX-VolItem" key={label}>
                    <b>{typeof value === "number" ? value.toLocaleString() : value}</b>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* What this integration is allowed to move, in plain words.
                Stated on the page rather than buried in code, because the
                question "what do we hand to them, and what do they hand us"
                should be answerable by looking, not by asking an engineer. */}
            {(!!row.sends?.length || !!row.receives?.length) && (
              <div className="AdmX-Exch">
                <div>
                  <h4>Intranet sends</h4>
                  <ul>
                    {row.sends.map((line) => (
                      <li key={line} className={/^(nothing|never)/i.test(line) ? "never" : undefined}>{line}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4>Intranet receives</h4>
                  <ul>
                    {row.receives.map((line) => (
                      <li key={line} className={/^(nothing|never)/i.test(line) ? "never" : undefined}>{line}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// A superadmin viewing the app as someone else goes through the same Django
// login path as a real sign-in, so those rows used to be indistinguishable from
// that person actually signing in. They're labelled now — the row reads
// "bassi → Karishmajoshi" rather than looking like Karishma logged in herself.
const EVENT_LABEL = {
  login: "login",
  failed: "failed",
  login_as: "login as",
  exit_login_as: "exit login as",
};
const EVENT_HINT = {
  login_as: "A superadmin started viewing the app as this user — not a real sign-in by them.",
  exit_login_as: "A superadmin stopped viewing as another user and returned to their own account.",
};

function AuditView({ audit }) {
  const [sort, setSort] = useState({ k: "at", dir: "desc" });
  const rows = useMemo(() => [...(audit || [])].sort((a, b) => {
    const x = a[sort.k] || "", y = b[sort.k] || "";
    const c = String(x).localeCompare(String(y));
    return sort.dir === "asc" ? c : -c;
  }), [audit, sort]);
  if (!audit) return <div className="AdmX-Loading">Loading…</div>;
  return (
    <div className="AdmX-View">
      <div className="AdmX-Head"><h1>Login activity</h1><span className="AdmX-Sub">{audit.length}</span></div>
      {!audit.length ? <div className="AdmX-Loading">Nothing yet — captured on the next login after deploy.</div> : (
        <div className="Table-Scroll"><table className="AdmX-Table">
          <thead><tr>
            <Th label="User" k="username" sort={sort} setSort={setSort} />
            <Th label="Event" k="event" sort={sort} setSort={setSort} />
            <Th label="IP" k="ip" sort={sort} setSort={setSort} />
            <Th label="When" k="at" sort={sort} setSort={setSort} align="r" />
          </tr></thead>
          <tbody>{rows.map((a, i) => (
            <tr key={i}><td className="nm"><b>{a.username || "—"}</b></td>
              <td><span className={"ev " + a.event} title={EVENT_HINT[a.event] || ""}>{EVENT_LABEL[a.event] || a.event}</span></td>
              <td className="sub">{a.ip || "—"}</td>
              <td className="r sub">{new Date(a.at).toLocaleString()}</td></tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

// Who gained or lost access to what -- team membership and GitHub repository
// access, merged by the backend into one chronological list with one row per
// person per repository per action. Raw, that is a wall: adding one person to a
// project with six repositories is six near-identical rows. So it is read here
// the way a person would say it -- one sentence per person per action, the
// repositories tucked in as chips -- grouped by day, with anything that FAILED
// pulled to the top as its own count, because a failed grant is the one row in
// here that someone has to act on.
const ACCESS_WORDS = {
  "Access granted": { verb: "was given access to", subject: "repo" },
  "Invited": { verb: "was invited to", subject: "repo" },
  "Grant failed": { verb: "could NOT be given access to", subject: "repo" },
  "Access revoked": { verb: "lost access to", subject: "repo" },
  "Revoke failed": { verb: "could NOT be removed from", subject: "repo" },
  "Revoke skipped — repository not found": { verb: "needed no change on", subject: "repo" },
  "Stale invite reissued": { verb: "had an expired invite re-sent for", subject: "repo" },
  "Added to team": { verb: "was added to the", subject: "team" },
  "Removed from team": { verb: "was removed from the", subject: "team" },
  "Replaced": { verb: "was replaced on the", subject: "team" },
  "Added back": { verb: "was added back to the", subject: "team" },
  "Status changed": { verb: "had their status changed on the", subject: "team" },
};
const GROUP_WINDOW_MS = 20 * 60 * 1000;
const ACCESS_GROUPS_SHOWN = 40;

function accessDayLabel(date) {
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

// Rows are newest-first. Consecutive rows that are the same person, project,
// action and actor, close together in time, are one thing that happened.
function groupAccessEvents(events) {
  const sorted = [...events].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  const groups = [];
  for (const e of sorted) {
    const last = groups[groups.length - 1];
    const sameThing = last
      && last.kind === e.kind && last.employee === (e.employee_name || "")
      && last.project === (e.project_name || "") && last.event === e.event
      && last.actor === (e.actor_name || "") && last.tone === e.tone
      && Math.abs(new Date(last.latestAt) - new Date(e.at)) <= GROUP_WINDOW_MS;
    if (sameThing) {
      if (e.repository_name && !last.repos.includes(e.repository_name)) last.repos.push(e.repository_name);
      if (e.detail && !last.details.includes(e.detail)) last.details.push(e.detail);
      last.count += 1;
    } else {
      groups.push({
        kind: e.kind, employee: e.employee_name || "", project: e.project_name || "", event: e.event,
        actor: e.actor_name || "", tone: e.tone, latestAt: e.at,
        repos: e.repository_name ? [e.repository_name] : [], details: e.detail ? [e.detail] : [], count: 1,
      });
    }
  }
  return groups;
}

function AccessGroup({ g }) {
  const [open, setOpen] = useState(false);
  const words = ACCESS_WORDS[g.event] || { verb: g.event.toLowerCase(), subject: "repo" };
  const repoCount = g.repos.length;
  const shownRepos = open ? g.repos : g.repos.slice(0, 3);
  const when = new Date(g.latestAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const tone = g.tone === "failed" ? "failed" : g.tone === "neutral" ? "neutral" : "ok";

  let target;
  if (words.subject === "team") target = <> <b>{g.project || "project"}</b> team</>;
  else if (repoCount === 0) target = <> a repository{g.project ? <> in <b>{g.project}</b></> : null}</>;
  else if (repoCount === 1) target = <> <b>{g.repos[0]}</b>{g.project ? <> <span className="AdmX-Act-In">({g.project})</span></> : null}</>;
  else target = <> <b>{repoCount} repositories</b>{g.project ? <> in <b>{g.project}</b></> : null}</>;

  return (
    <div className={"AdmX-Act-Card " + tone}>
      <div className="AdmX-Act-Main">
        <span className="AdmX-Act-Dot" />
        <div className="AdmX-Act-Body">
          <p className="AdmX-Act-Line">
            <b>{g.employee || "Someone"}</b> {words.verb}{target}
            {g.actor ? <span className="AdmX-Act-By"> · by {g.actor}</span> : <span className="AdmX-Act-By"> · automatic</span>}
          </p>
          {repoCount > 1 && (
            <div className="AdmX-Act-Repos">
              {shownRepos.map((r) => <span key={r} className="AdmX-Act-Chip">{r}</span>)}
              {repoCount > 3 && (
                <button type="button" className="AdmX-Act-More" onClick={() => setOpen((v) => !v)}>
                  {open ? "show less" : `+${repoCount - 3} more`}
                </button>
              )}
            </div>
          )}
          {tone === "failed" && g.details[0] && <p className="AdmX-Act-Why">{g.details[0]}</p>}
          {tone !== "failed" && g.kind === "team" && g.details[0] && <p className="AdmX-Act-Note">{g.details[0]}</p>}
        </div>
        <span className="AdmX-Act-Time">{when}</span>
      </div>
    </div>
  );
}

function AccessActivityView({ events }) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(ACCESS_GROUPS_SHOWN);

  const groups = useMemo(() => groupAccessEvents(events || []), [events]);
  const failed = groups.filter((g) => g.tone === "failed");
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups.filter((g) => {
      if (filter === "failed" && g.tone !== "failed") return false;
      if (filter === "github" && g.kind !== "access") return false;
      if (filter === "team" && g.kind !== "team") return false;
      if (!q) return true;
      return [g.employee, g.project, g.actor, ...g.repos].some((x) => String(x || "").toLowerCase().includes(q));
    });
  }, [groups, filter, query]);

  const days = [];
  for (const g of visible.slice(0, limit)) {
    const label = accessDayLabel(g.latestAt);
    if (!days.length || days[days.length - 1].label !== label) days.push({ label, items: [] });
    days[days.length - 1].items.push(g);
  }
  const FILTERS = [["all", "Everything"], ["failed", `Needs attention${failed.length ? ` (${failed.length})` : ""}`], ["github", "GitHub access"], ["team", "Team changes"]];

  if (!events) return <div className="AdmX-Loading">Loading…</div>;
  return (
    <div className="AdmX-View">
      <div className="AdmX-Head"><h1>Access activity</h1><span className="AdmX-Sub">latest {events.length} changes, grouped</span></div>
      {!events.length ? <div className="AdmX-Loading">Nothing yet — captured the next time someone's project or repository access changes.</div> : (
        <>
          {failed.length > 0 ? (
            <button type="button" className="AdmX-Act-Alert" onClick={() => setFilter("failed")}>
              <b>{failed.length}</b> {failed.length === 1 ? "change" : "changes"} did not go through — GitHub refused or errored. Tap to see them.
            </button>
          ) : (
            <p className="AdmX-Act-Clear">Nothing failed in this stretch — every grant and removal went through.</p>
          )}
          <div className="AdmX-Act-Tools">
            <div className="AdmX-Act-Filters">
              {FILTERS.map(([key, label]) => (
                <button key={key} type="button" className={"AdmX-Act-Filter" + (filter === key ? " on" : "")} onClick={() => { setFilter(key); setLimit(ACCESS_GROUPS_SHOWN); }}>{label}</button>
              ))}
            </div>
            <input className="AdmX-Act-Search" value={query} onChange={(e) => { setQuery(e.target.value); setLimit(ACCESS_GROUPS_SHOWN); }} placeholder="Search a person, project or repository…" />
          </div>
          {!visible.length && <div className="AdmX-Loading">Nothing matches that.</div>}
          {days.map((day) => (
            <section key={day.label} className="AdmX-Act-Day">
              <h3>{day.label}</h3>
              {day.items.map((g, i) => <AccessGroup key={`${g.latestAt}-${i}`} g={g} />)}
            </section>
          ))}
          {visible.length > limit && (
            <button type="button" className="Outline-Button AdmX-Act-Showmore" onClick={() => setLimit((n) => n + ACCESS_GROUPS_SHOWN)}>
              Show {Math.min(ACCESS_GROUPS_SHOWN, visible.length - limit)} more
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ── Defaulters: who is skipping the task code in commits / not filing EODs ──
   One row per person, newest slip first. No daily alert goes to super-admin for
   any of this; this page is where it is read. Zero is a blank cell, not "0", and
   the cell stays in its column so the table keeps its shape. */
const DEFAULTER_WINDOWS = [[7, "7 days"], [30, "30 days"], [90, "90 days"]];

function defaultAgo(iso) {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

function DefaultersView({ report, days, setDays }) {
  const [open, setOpen] = useState(null);
  const people = report?.people || [];
  const unmapped = report?.unmapped || [];
  const commits = people.reduce((n, p) => n + p.github.count, 0);
  const eods = people.reduce((n, p) => n + p.eod.count, 0);

  return (
    <div className="AdmX-View">
      <div className="AdmX-Head">
        <div>
          <h1>Defaulters</h1>
          <span className="AdmX-Sub">Commits pushed without a task code, and EODs not filed. Each person is told once a day; you are not.</span>
        </div>
        <div className="AdmX-AiRange">
          {DEFAULTER_WINDOWS.map(([n, label]) => (
            <button key={n} type="button" className={"AdmX-AiRangeBtn" + (days === n ? " on" : "")} onClick={() => setDays(n)}>{label}</button>
          ))}
        </div>
      </div>

      {!report ? <div className="AdmX-Loading">Loading…</div> : (
        <>
          {!people.length && !unmapped.length ? (
            <p className="AdmX-Act-Clear">No defaults in the last {days} days.</p>
          ) : people.length > 0 && (
            <p className="AdmX-Act-Clear">{people.length} {people.length === 1 ? "person" : "people"} slipped in the last {days} days, most recent first.</p>
          )}

          {people.length > 0 && (
            <div className="Table-Scroll">
              <table className="AdmX-Table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Department</th>
                    <th className="r">Commits without a task code{commits ? ` (${commits})` : ""}</th>
                    <th className="r">EODs missed{eods ? ` (${eods})` : ""}</th>
                    <th>Last default</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => {
                    const isOpen = open === p.employee_id;
                    return (
                      <React.Fragment key={p.employee_id}>
                        <tr className="AdmX-Def-Row" onClick={() => setOpen(isOpen ? null : p.employee_id)}>
                          <td className="nm"><b>{p.name}</b></td>
                          <td className="sub">{p.department || "—"}</td>
                          <td className="r">{p.github.count || ""}</td>
                          <td className="r">{p.eod.count || ""}</td>
                          <td>{defaultAgo(p.last_default_at)}</td>
                        </tr>
                        {isOpen && (
                          <tr className="AdmX-Def-Detail">
                            <td colSpan={5}>
                              {p.github.recent.length > 0 && (
                                <div className="AdmX-Def-Block">
                                  <h4>Recent commits with no task code</h4>
                                  <ul>
                                    {p.github.recent.map((c) => (
                                      <li key={c.sha + c.repository}>
                                        <a href={c.url} target="_blank" rel="noreferrer">{c.sha}</a>
                                        <span>{c.message || "(no message)"}</span>
                                        <small>{c.repository}{c.branch ? ` · ${c.branch}` : ""} · {defaultAgo(c.at)}</small>
                                      </li>
                                    ))}
                                  </ul>
                                  {p.github.count > p.github.recent.length && <small>and {p.github.count - p.github.recent.length} more</small>}
                                </div>
                              )}
                              {p.eod.dates.length > 0 && (
                                <div className="AdmX-Def-Block">
                                  <h4>Days with no EOD</h4>
                                  <p>{p.eod.dates.map((d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" })).join(", ")}{p.eod.count > p.eod.dates.length ? ` and ${p.eod.count - p.eod.dates.length} earlier` : ""}</p>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {unmapped.length > 0 && (
            <div className="AdmX-Def-Unmapped">
              <h3>Pushed by GitHub accounts we cannot match to anyone</h3>
              <p>Nobody has these GitHub usernames on their profile, so no one can be told. Add the username to the right person and the next scan will attribute them.</p>
              <ul>
                {unmapped.map((u) => (
                  <li key={u.github_login}><b>{u.github_login}</b><span>{u.count} {u.count === 1 ? "commit" : "commits"}</span><small>{defaultAgo(u.last_at)}</small></li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── AI Usage: estimated LLM cost/tokens per day / model / purpose ── */
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: n < 1 ? 4 : 2, maximumFractionDigits: 4 })}`;
const compactNum = (n) => Number(n || 0).toLocaleString();

function AiUsageBars({ data }) {
  const max = Math.max(...data.map((d) => d.cost), 0.0000001);
  return (
    <div className="AdmX-AiChart">
      <div className="AdmX-AiBars">
        {data.map((d) => (
          <div key={d.date} className="AdmX-AiBarCol" title={`${d.date} · ${usd(d.cost)} · ${d.calls} calls`}>
            <div className="AdmX-AiBar" style={{ height: `${Math.max(2, (d.cost / max) * 100)}%` }} />
            <span className="AdmX-AiBarLbl">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AiUsageView({ usage, days, setDays }) {
  if (!usage) return <div className="AdmX-Loading">Loading AI usage…</div>;
  const t = usage.totals || {};
  return (
    <div className="AdmX-View">
      <div className="AdmX-Head">
        <div>
          <h1>AI Models · Usage</h1>
          <span className="AdmX-Sub">Where LLM tokens go, per day / model / purpose. Cost is estimated from public rates.</span>
        </div>
        <div className="AdmX-AiRange">
          {[7, 14, 30].map((d) => (
            <button key={d} className={"AdmX-AiRangeBtn" + (days === d ? " on" : "")} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </div>

      <div className="AdmX-AiStats">
        <div className="AdmX-AiStat"><span className="AdmX-AiStatLbl">Est. cost · {usage.days}d</span><strong>{usd(t.cost)}</strong></div>
        <div className="AdmX-AiStat"><span className="AdmX-AiStatLbl">LLM calls</span><strong>{compactNum(t.calls)}</strong></div>
        <div className="AdmX-AiStat"><span className="AdmX-AiStatLbl">Tokens in</span><strong>{compactNum(t.input_tokens)}</strong><span className="AdmX-AiStatSub">{compactNum(t.output_tokens)} out</span></div>
        <div className="AdmX-AiStat"><span className="AdmX-AiStatLbl">Avg / day</span><strong>{usd(t.avg_per_day)}</strong><span className="AdmX-AiStatSub">estimated</span></div>
      </div>

      <div className="AdmX-AiChartCard">
        <div className="AdmX-AiChartTitle">Estimated cost per day (USD)</div>
        <AiUsageBars data={usage.per_day || []} />
      </div>

      <div className="AdmX-AiSplit">
        <div className="AdmX-AiPanel">
          <div className="AdmX-AiPanelTitle">By model</div>
          {(usage.by_model || []).length ? (usage.by_model || []).map((m) => (
            <div key={m.model} className="AdmX-AiRow">
              <span className="AdmX-AiRowName mono">{m.model}</span>
              <span className="AdmX-AiRowMeta">{compactNum(m.calls)} calls · {compactNum(m.tokens)} tok</span>
              <span className="AdmX-AiRowCost">{usd(m.cost)}</span>
            </div>
          )) : <div className="AdmX-Empty">No AI calls in this window.</div>}
        </div>
        <div className="AdmX-AiPanel">
          <div className="AdmX-AiPanelTitle">By purpose</div>
          {(usage.by_purpose || []).length ? (usage.by_purpose || []).map((p) => (
            <div key={p.purpose} className="AdmX-AiRow">
              <span className="AdmX-AiRowName">{p.purpose}</span>
              <span className="AdmX-AiRowMeta">{compactNum(p.calls)} calls</span>
              <span className="AdmX-AiRowCost">{usd(p.cost)}</span>
            </div>
          )) : <div className="AdmX-Empty">No AI calls in this window.</div>}
        </div>
      </div>
      <p className="AdmX-AiFoot">Cost is an estimate from public per-token rates · not a bill.</p>
    </div>
  );
}

// Endpoint Usage — built for a concrete question, not just general
// visibility: when two live code paths do the same job differently (see
// the PTRC professional-tax discrepancy, GitHub issue #128), this is how
// you tell which one real traffic actually goes through, instead of
// guessing. Default order is already "most used first" (the backend sorts
// by hit_count); clicking a row expands its per-user breakdown rather than
// listing every user inline, which would bury the one number that matters
// most — how often this endpoint gets hit at all — under a wall of names.
export function EndpointUsageView({ usage, days, setDays }) {
  const [open, setOpen] = useState(null);
  if (!usage) return <div className="AdmX-Loading">Loading endpoint usage…</div>;
  const rows = usage.endpoints || [];
  const totalHits = rows.reduce((sum, r) => sum + r.hit_count, 0);
  const totalErrors = rows.reduce((sum, r) => sum + r.error_count, 0);
  // Access control correctly saying no (401/403) is not a failure — a page
  // gated to superusers answering 403 to every other signed-in employee who
  // ever loads it is the system working, not something broken. Counted and
  // shown, just not as an alarm next to things that actually are broken.
  const totalDenied = rows.reduce((sum, r) => sum + (r.denied_count || 0), 0);

  return (
    <div className="AdmX-View">
      <div className="AdmX-Head">
        <div>
          <h1>Endpoint Usage</h1>
          <span className="AdmX-Sub">Every API endpoint, sorted by how often it's actually hit — who's calling it, and how fast it answers.</span>
        </div>
        <div className="AdmX-AiRange">
          {[7, 14, 30, 90].map((d) => (
            <button key={d} className={"AdmX-AiRangeBtn" + (days === d ? " on" : "")} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </div>

      <div className="AdmX-AiStats">
        <div className="AdmX-AiStat"><span className="AdmX-AiStatLbl">Endpoints seen · {usage.days}d</span><strong>{rows.length}</strong></div>
        <div className="AdmX-AiStat"><span className="AdmX-AiStatLbl">Total requests</span><strong>{totalHits.toLocaleString()}</strong></div>
        <div className="AdmX-AiStat"><span className="AdmX-AiStatLbl">Errors</span><strong>{totalErrors.toLocaleString()}</strong></div>
        {/* Omitted entirely when zero, same as the stat cards elsewhere in
            this app — a page gated to superusers answering 403 to every
            other employee who loads it isn't an error, it's access control
            working, so it doesn't belong next to real failures above. */}
        {totalDenied > 0 && (
          <div className="AdmX-AiStat"><span className="AdmX-AiStatLbl">Access denied (expected)</span><strong style={{ color: "var(--c-500)" }}>{totalDenied.toLocaleString()}</strong></div>
        )}
      </div>

      {rows.length ? (
        <div className="Table-Scroll">
          <table className="AdmX-Table">
            <thead>
              <tr>
                <th style={{ width: 20 }} />
                <th>Endpoint</th>
                <th className="r">Hits</th>
                <th className="r">Avg response</th>
                <th className="r">Errors</th>
                <th className="r">Unique users</th>
                <th>Last hit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = `${r.method} ${r.path_pattern}`;
                const isOpen = open === key;
                return (
                  <React.Fragment key={key}>
                    <tr onClick={() => setOpen(isOpen ? null : key)} style={{ cursor: "pointer" }}>
                      <td>{isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                      <td className="nm">
                        <b>{r.path_pattern}</b>
                        <i>{r.method}{r.view_name ? ` · ${r.view_name}` : ""}</i>
                      </td>
                      <td className="r">{r.hit_count.toLocaleString()}</td>
                      <td className="r">{r.average_duration_ms} ms</td>
                      <td className="r">
                        {r.error_count
                          ? <span style={{ color: "var(--red-600)", fontWeight: 700 }}>{r.error_count}</span>
                          : <em>0</em>}
                        {/* A quiet footnote, not a second alarming number —
                            access control correctly saying no to people who
                            can't see this endpoint, not something broken. */}
                        {r.denied_count > 0 && (
                          <span style={{ display: "block", fontSize: 11, color: "var(--c-400)", fontWeight: 400 }}>
                            {r.denied_count} denied
                          </span>
                        )}
                      </td>
                      <td className="r">{r.unique_users}</td>
                      <td className="sub">{timeAgo(r.last_hit_at)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} style={{ background: "var(--surface-soft)", padding: "10px 14px 14px 44px" }}>
                          {r.top_companies.length ? (
                            <div className="AdmX-EndpointUsers">
                              {r.top_companies.map((c) => (
                                <span key={c.company} className="AdmX-EndpointUserChip">
                                  {c.company} <b>{c.hit_count.toLocaleString()}</b>
                                </span>
                              ))}
                            </div>
                          ) : <span className="AdmX-Empty">No signed-in activity on this endpoint in this window.</span>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <div className="AdmX-Empty">No endpoint traffic recorded yet in this window.</div>}
    </div>
  );
}

// Same instant, fixed-position hover pattern as ConnectionHoverTip above —
// built generic here since the comparison table needs it on ~300 small
// chips (18 vendors x 20 categories), where a wall of always-visible text
// would defeat the point of a scannable table. One line of detail per
// vendor per category lives behind this icon; the table itself only ever
// shows the tier (core/common/niche/not offered).
function InfoHoverTip({ text }) {
  const [pos, setPos] = useState(null);
  const ref = React.useRef(null);
  if (!text) return null;
  const show = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 320;
    const margin = 12;
    let left = rect.left;
    if (left + width > window.innerWidth - margin) left = window.innerWidth - width - margin;
    if (left < margin) left = margin;
    let top = rect.bottom + 6;
    const estimated = 120;
    if (top + estimated > window.innerHeight - margin) top = Math.max(margin, rect.top - estimated);
    setPos({ top, left });
  };
  return (
    <span className="AdmX-InfoWrap" onMouseEnter={show} onMouseLeave={() => setPos(null)}>
      <Info ref={ref} size={13} className="AdmX-InfoIcon" />
      {pos && createPortal(
        <div className="AdmX-InfoTip" style={{ top: pos.top, left: pos.left, zIndex: 4000 }}>
          {text}
        </div>,
        document.body,
      )}
    </span>
  );
}

function TierMark({ tier }) {
  if (!tier) return <span className="AdmX-Cmp-Tier not" title="Not offered">—</span>;
  const title = tier === "core" ? "Core — nearly universal" : tier === "common" ? "Common — widely adopted" : "Niche — narrow adoption";
  return <span className={"AdmX-Cmp-Tier " + tier} title={title} />;
}

// Feature-level matrix: every row is one named, discrete capability (not a
// whole module) so "do we have Payroll" can't quietly stand in for "do we
// have the 14 things a real payroll module does." Vendor cells are a plain
// tier dot — the feature label itself carries the explanation now, so a
// hover tooltip per vendor per feature would just be noise on top of noise.
// The "Ours" column is the one that matters most and is the one with real
// notes, via the same InfoHoverTip used elsewhere.
function ModuleComparisonRow({ category, expanded, onToggle }) {
  const total = featureCount(category);
  const have = oursCount(category);
  const vendors = vendorsFor(category);
  return (
    <div className="AdmX-Cmp-Row">
      <button className="AdmX-Cmp-Head" onClick={onToggle}>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        <div className="AdmX-Cmp-HeadMain">
          <strong>{category.label}</strong>
          <span className="AdmX-Cmp-Desc">{category.description}</span>
        </div>
        <span className="AdmX-Cmp-FeatCount">{have}/{total} features</span>
      </button>
      {expanded && (
        <div className="AdmX-Cmp-TableWrap">
          <table className="AdmX-Cmp-Table">
            <thead>
              <tr>
                <th className="AdmX-Cmp-FeatCol">Feature</th>
                {vendors.map((v) => (
                  <th key={v.key} className="AdmX-Cmp-VCol" title={v.name}>{v.code}</th>
                ))}
                <th className="AdmX-Cmp-OursCol">Ours</th>
              </tr>
            </thead>
            <tbody>
              {category.features.map((f) => (
                <tr key={f.key}>
                  <td className="AdmX-Cmp-FeatCol">{f.label}</td>
                  {vendors.map((v) => (
                    <td key={v.key} className="AdmX-Cmp-VCol"><TierMark tier={f.vendors[v.key]} /></td>
                  ))}
                  <td className="AdmX-Cmp-OursCol">
                    <span className={"AdmX-Cmp-OursMark" + (f.ours ? " yes" : " no")}>
                      {f.ours ? <Check size={14} /> : <X size={13} />}
                    </span>
                    <InfoHoverTip text={f.oursNote} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Industry Comparison — rows ordered by real adoption (most-universal
// category first), not by our own priorities; see ModuleComparisonContent.js
// for the research behind every cell, including which "ours" flags were
// confirmed against the actual code vs. which categories are still a
// lighter first pass (category.depth: "deep" | "light") pending more
// research. The point is an honest, feature-level map of what the industry
// builds versus what we've actually built — not a module-level checkbox
// that hides how thin a single green tick can be.
export function ModuleComparisonView() {
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const rows = needle
    ? CATEGORIES.filter((c) => {
        const vendors = vendorsFor(c).map((v) => `${v.name} ${v.code}`).join(" ");
        const hay = [c.label, c.description, ...(c.aliases || []), vendors, ...c.features.map((f) => f.label)].join(" ").toLowerCase();
        return hay.includes(needle);
      })
    : CATEGORIES;

  return (
    <div className="AdmX-View">
      <div className="AdmX-Head">
        <div>
          <h1>Industry Comparison</h1>
          <span className="AdmX-Sub">
            Feature by feature. Each row is compared with the five products that lead that specific job, not one HR suite scored against everything.
            Click a row to open it. Hover a column code for the product name, and the{" "}
            <Info size={11} style={{ verticalAlign: -1 }} /> next to Ours for why.
          </span>
        </div>
      </div>
      <div className="AdmX-Filters" style={{ marginBottom: 14 }}>
        <div className="AdmX-SearchBox"><Search size={15} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search capability areas or specific features…" /></div>
      </div>
      <div className="AdmX-Cmp-Legend">
        <span><i className="AdmX-Cmp-Dot core" /> Core — nearly every customer has it</span>
        <span><i className="AdmX-Cmp-Dot common" /> Common — widely adopted, sometimes tier-gated</span>
        <span><i className="AdmX-Cmp-Dot niche" /> Niche — exists, narrow adoption</span>
        <span>— Not offered</span>
      </div>
      <div className="AdmX-Cmp-List">
        {rows.map((c) => (
          <ModuleComparisonRow
            key={c.key}
            category={c}
            expanded={open === c.key}
            onToggle={() => setOpen(open === c.key ? null : c.key)}
          />
        ))}
      </div>
    </div>
  );
}

function AccessDrawer({ user, matrix, onClose, act }) {
  const actions = matrix?.actions || ["create", "read", "update", "delete"];
  const pages = matrix?.pages || [];
  const grid = user.page_access || {};
  // Exceptions this person carries, so the grid can show what was set by hand.
  const exceptions = {};
  for (const row of user.page_overrides || []) {
    exceptions[`${row.page}.${row.action}`] = row.allow;
  }

  useEffect(() => {
    const onKey2 = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey2);
    return () => document.removeEventListener("keydown", onKey2);
  }, [onClose]);

  return (
    <div className="AdmX-DrawerWrap" onClick={onClose}>
      <div className="AdmX-Drawer" onClick={(e) => e.stopPropagation()}>
        <div className="AdmX-DrawerHead">
          <div><b>{user.name}</b><i>{user.username} · {user.department || "no dept"} · {user.employment_type || "—"}</i></div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!user.is_superuser && !user.detailLoading && (user.page_overrides?.length > 0 || user.overrides?.length > 0) && (
              <button
                title="Clear every hand-set page flag and legacy grant — back to exactly what their department gives them, as if freshly onboarded"
                onClick={() => {
                  if (window.confirm(`Reset ${user.name} to their department's baseline access? This clears every permission set by hand for them.`)) {
                    act(user.id, { action: "reset_page_overrides" });
                  }
                }}
                style={{ fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 7, border: "1px solid var(--c-200)", background: "transparent", color: "var(--c-600)", cursor: "pointer" }}
              >
                Reset to baseline
              </button>
            )}
            <button onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        {user.is_superuser ? (
          <div className="AdmX-Super"><Shield size={16} /> Superuser — full access, bypasses the model.</div>
        ) : user.detailLoading ? (
          <div className="AdmX-Loading">Loading access…</div>
        ) : user.detailError ? (
          <div className="AdmX-Err">{user.detailError}</div>
        ) : (
          <div className="AdmX-DrawerBody">
            <p className="AdmX-Rule">
              Tick a box to allow, click again to deny, click a third time to hand the
              decision back to their department. Read is the gate — clear it and the page
              disappears for them, and its API refuses them too. A <b style={{ color: "var(--blue-700,#1d4ed8)" }}>blue</b> tick
              was added by hand, beyond what their department gives everyone in it.
            </p>
            <table className="AdmX-PageGrid editable">
              <thead>
                <tr>
                  <th>Page</th>
                  {actions.map((a) => <th key={a}>{a[0].toUpperCase()}</th>)}
                </tr>
              </thead>
              <tbody>
                {pages.map((p) => {
                  const has = grid[p.page] || [];
                  return (
                    <tr key={p.page} className={has.length ? "" : "muted"}>
                      <td title={p.note || p.label}>
                        {p.label}
                        {p.self_service && <span className="AdmX-SelfTag" title="Their own records — everyone has this">own</span>}
                      </td>
                      {actions.map((a) => {
                        if (!p.supports.includes(a)) {
                          return <td key={a} className="AdmX-Flag"><span className="AdmX-NA" title={`${p.label} has no ${a} to grant`}>·</span></td>;
                        }
                        const on = has.includes(a);
                        const exception = exceptions[`${p.page}.${a}`];
                        const next = exception === undefined
                          ? { action: "set_page_flag", page: p.page, page_action: a, allow: !on }
                          : exception === true
                            ? { action: "set_page_flag", page: p.page, page_action: a, allow: false }
                            : { action: "clear_page_flag", page: p.page, page_action: a };
                        return (
                          <td key={a} className="AdmX-Flag">
                            <button
                              className={"AdmX-FlagBtn" + (on ? " on" : "") + (exception !== undefined ? " manual" : "")}
                              title={
                                exception === true ? `Allowed by hand — click to deny`
                                : exception === false ? `Denied by hand — click to follow their department again`
                                : on ? `From their department — click to deny` : `Not granted — click to allow`
                              }
                              onClick={() => act(user.id, next)}
                            >{on ? "✓" : "–"}</button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
